import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, linkedSignal, resource, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ImageUploadModule } from "../../modules/s25-image-upload/s25.image.upload.module";
import { ImageUploadModelI } from "../../modules/s25-image-upload/s25.image.upload.component";
import { S25InfoMessageModule } from "../../modules/s25-info-message/s25.info.message.module";
import { S25LoadingInlineModule } from "../../modules/s25-loading/s25.loading.inline.module";
import { CacheRepository } from "../../decorators/cache.decorator";
import { InstCssService } from "../../services/intscss.service";
import { S25Util } from "../../util/s25-util";

type ThemeColorKey = "backgroundColor" | "bottomBorderColor" | "textColor";

type InstCssData = {
    logoBase64?: string;
    logoType?: string;
    logoWidth?: string | number;
    backgroundColor?: string;
    bottomBorderColor?: string;
    textColor?: string;
    altText?: string;
};

type ThemeAttributes = {
    logoBase64: string;
    logoType: string;
    logoWidth: number;
    backgroundColor: string;
    bottomBorderColor: string;
    textColor: string;
    altText: string;
};

const HEX_REGEX = /^#[0-9A-F]{6}$/i;
const ALT_TEXT_MAX_LENGTH = 250;

@TypeManagerDecorator("s25-ng-configure-theme")
@Component({
    selector: "s25-ng-configure-theme",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ImageUploadModule, S25InfoMessageModule, S25LoadingInlineModule],
    template: `
        <div class="configure-theme-wrapper">
            <h2 class="c-margin-bottom--single">Configure Theme</h2>
            @if (instCssResource.isLoading() && !instCssResource.hasValue()) {
                <s25-ng-loading-inline-static />
            } @else {
                <div class="c-margin-bottom--single">
                    <p class="ngBold c-margin-bottom--quarter">Logo</p>
                    <div class="ngFinePrint c-margin-bottom--half">
                        Max width of 250 pixels and fixed height of 60 pixels.
                    </div>
                    <s25-ng-image-upload
                        [(model)]="imageModel"
                        [hasCropper]="true"
                        [imageUri]="logoImageUri()"
                        [imgWidth]="logoWidthPx()"
                        [maxWidth]="250"
                        [maxHeight]="60"
                        [minHeight]="60"
                        (uploadedImageChange)="setUploadedLogoData($event)"
                    />
                    <label for="theme-description">Image Description</label>
                    <s25-ng-info-message [customLabel]="'logo image'" [open]="false">
                        Adding a description of your logo provides more context and makes it accessible to users with
                        disabilities. The best descriptions are concise and should include any text in the image.
                    </s25-ng-info-message>
                    <p class="ngFinePrint">{{ charsRemaining() }} / {{ maxAltTextLength }} characters remaining</p>
                    <textarea
                        id="theme-description"
                        class="cn-form__control cn-form__control-stretch"
                        [ngModel]="altText()"
                        (ngModelChange)="setAltText($event)"
                        [attr.maxlength]="maxAltTextLength"
                    ></textarea>
                </div>

                <div class="c-margin-bottom--single color-picker-set">
                    <label class="ngBold c-margin-bottom--quarter" for="backgroundColor">Background Color</label>
                    <input
                        id="backgroundColor"
                        type="color"
                        class="ngInlineBlock"
                        [ngModel]="S25Util.hexForColorPicker(backgroundColor(), '#46587c')"
                        (ngModelChange)="setColor('backgroundColor', $event)"
                    />
                    <input
                        type="text"
                        class="c-input c-margin-top--quarter"
                        [class.error-outline]="hexError() === 'backgroundColor'"
                        [ngModel]="backgroundColor()"
                        (ngModelChange)="setColor('backgroundColor', $event)"
                    />
                    @if (hexError() === "backgroundColor") {
                        <p class="error-message">Invalid Hex Code</p>
                    }
                </div>

                <div class="c-margin-bottom--single color-picker-set">
                    <label class="ngBold c-margin-bottom--quarter" for="borderColor">Top Border Color</label>
                    <input
                        id="borderColor"
                        type="color"
                        class="ngInlineBlock"
                        [ngModel]="S25Util.hexForColorPicker(bottomBorderColor(), '#04345d')"
                        (ngModelChange)="setColor('bottomBorderColor', $event)"
                    />
                    <input
                        type="text"
                        class="c-input c-margin-top--quarter"
                        [class.error-outline]="hexError() === 'bottomBorderColor'"
                        [ngModel]="bottomBorderColor()"
                        (ngModelChange)="setColor('bottomBorderColor', $event)"
                    />
                    @if (hexError() === "bottomBorderColor") {
                        <p class="error-message">Invalid Hex Code</p>
                    }
                </div>

                <div class="c-margin-bottom--single color-picker-set">
                    <label class="ngBold c-margin-bottom--quarter" for="textColor">Text Color</label>
                    <input
                        id="textColor"
                        type="color"
                        class="ngInlineBlock"
                        [ngModel]="S25Util.hexForColorPicker(textColor(), '#ffffff')"
                        (ngModelChange)="setColor('textColor', $event)"
                    />
                    <input
                        type="text"
                        class="c-input c-margin-top--quarter"
                        [class.error-outline]="hexError() === 'textColor'"
                        [ngModel]="textColor()"
                        (ngModelChange)="setColor('textColor', $event)"
                    />
                    @if (hexError() === "textColor") {
                        <p class="error-message">Invalid Hex Code</p>
                    }
                </div>

                <div class="c-margin-top--single theme-action-buttons">
                    <button
                        class="aw-button aw-button--primary"
                        (click)="publish()"
                        [disabled]="saving() || !hasUnsavedChanges() || !!hexError()"
                    >
                        Save Theme
                    </button>
                    <button class="aw-button aw-button--outline" (click)="preview()" [disabled]="saving()">
                        Preview Theme
                    </button>
                    <button class="aw-button aw-button--outline" (click)="reset()" [disabled]="saving()">
                        Reset Changes
                    </button>
                </div>

                @if (errorText()) {
                    <p class="error-message c-margin-top--single">{{ errorText() }}</p>
                }
                @if (successText()) {
                    <p class="ngGreen ngBold cn-alert cn-alert--success c-margin-top--single" role="status">
                        {{ successText() }}
                    </p>
                }
            }
        </div>
    `,
    styles: `
        .configure-theme-wrapper {
            max-width: 700px;
        }

        .cn-form__control {
            width: 100%;
        }

        .color-picker-set {
            display: flex;
            flex-direction: column;
        }

        .color-picker-set input[type="text"] {
            max-width: 7em;
        }

        .error-outline {
            outline: 2px solid var(--color--danger);
            outline-offset: 2px;
        }

        .error-message {
            color: var(--color--danger);
            font-size: 0.8em;
            margin-top: 5px;
        }

        .theme-action-buttons {
            display: flex;
            gap: 5px;
            flex-wrap: wrap;
        }
    `,
})
export class S25ConfigureThemeComponent {
    readonly S25Util = S25Util;
    readonly maxAltTextLength = ALT_TEXT_MAX_LENGTH;

    readonly imageModel: ImageUploadModelI = {};
    readonly saving = signal(false);
    readonly hexError = signal<ThemeColorKey | false>(false);
    readonly errorText = signal("");
    readonly successText = signal("");
    readonly uploadedLogoData = signal("");
    readonly logoImageUri = linkedSignal(() => {
        const cssData = this.instCssResource.value();
        const logoBase64 = cssData?.logoBase64 ?? "";
        const logoType = cssData?.logoType ?? "png";
        return logoBase64 ? S25Util.toBase64URI(logoBase64, logoType) : "";
    });
    readonly logoWidthPx = linkedSignal(() => this.normalizeLogoWidth(this.instCssResource.value()?.logoWidth));
    readonly altText = linkedSignal(() => (this.instCssResource.value()?.altText ?? "").slice(0, ALT_TEXT_MAX_LENGTH));
    readonly backgroundColor = linkedSignal(() =>
        this.normalizeHex(this.instCssResource.value()?.backgroundColor, "#46587c"),
    );
    readonly bottomBorderColor = linkedSignal(() =>
        this.normalizeHex(this.instCssResource.value()?.bottomBorderColor, "#04345d"),
    );
    readonly textColor = linkedSignal(() => this.normalizeHex(this.instCssResource.value()?.textColor, "#ffffff"));
    readonly persistedTheme = linkedSignal(() => this.mapInstCssDataToThemeAttributes(this.instCssResource.value()));

    readonly charsRemaining = computed(() => ALT_TEXT_MAX_LENGTH - this.altText().length);
    readonly hasUnsavedChanges = computed(() => {
        if (!this.instCssResource.hasValue()) {
            return false;
        }
        const persistedTheme = this.persistedTheme();
        const uploadedLogoData = this.uploadedLogoData();
        const hasLogoChange = uploadedLogoData
            ? this.normalizeLogoDataForComparison(uploadedLogoData) !==
              `${persistedTheme.logoType}|${persistedTheme.logoBase64}`
            : false;

        return (
            this.backgroundColor() !== persistedTheme.backgroundColor ||
            this.bottomBorderColor() !== persistedTheme.bottomBorderColor ||
            this.textColor() !== persistedTheme.textColor ||
            this.altText() !== persistedTheme.altText ||
            hasLogoChange
        );
    });

    readonly instCssResource = resource({
        loader: async () => {
            const response = await InstCssService.getInstCssCoalesed();
            return (response?.instCss ?? {}) as InstCssData;
        },
    });

    setAltText(value: string) {
        const nextValue = value ?? "";
        this.altText.set(nextValue.slice(0, ALT_TEXT_MAX_LENGTH));
        this.successText.set("");
    }

    setColor(colorKey: ThemeColorKey, value: string) {
        const normalizedValue = this.ensureHash(value);
        this.successText.set("");
        switch (colorKey) {
            case "backgroundColor":
                this.backgroundColor.set(normalizedValue);
                break;
            case "bottomBorderColor":
                this.bottomBorderColor.set(normalizedValue);
                break;
            case "textColor":
                this.textColor.set(normalizedValue);
                break;
        }
        this.hexError.set(HEX_REGEX.test(normalizedValue) ? false : colorKey);
    }

    setUploadedLogoData(imageData: string) {
        this.uploadedLogoData.set(imageData ?? "");
        this.successText.set("");
    }

    async preview() {
        if (this.hexError()) {
            return;
        }

        this.errorText.set("");
        try {
            const attributes = await this.getThemeAttributes();
            const instCssResponse = await InstCssService.getInstCss();
            instCssResponse.instCss = instCssResponse.instCss || {};
            instCssResponse.instCss.logoBase64 = attributes.logoBase64;
            instCssResponse.instCss.logoType = attributes.logoType;
            instCssResponse.instCss.logoWidth = `${attributes.logoWidth}px`;
            instCssResponse.instCss.backgroundColor = attributes.backgroundColor;
            instCssResponse.instCss.bottomBorderColor = attributes.bottomBorderColor;
            instCssResponse.instCss.textColor = attributes.textColor;
            instCssResponse.instCss.altText = attributes.altText;
            await InstCssService.setInstCss();
        } catch (error) {
            this.errorText.set(S25Util.errorText(error, "Failed to preview theme."));
        }
    }

    async publish() {
        if (this.hexError() || !this.hasUnsavedChanges()) {
            return;
        }

        this.errorText.set("");
        this.successText.set("");
        this.saving.set(true);
        try {
            const attributes = await this.getThemeAttributes();
            await InstCssService.saveInstCss(
                attributes.logoBase64,
                attributes.logoType,
                attributes.logoWidth,
                attributes.backgroundColor,
                attributes.bottomBorderColor,
                attributes.textColor,
                attributes.altText,
            );
            this.persistedTheme.set(attributes);
            await this.reset();
            this.successText.set("Theme saved successfully.");
        } catch (error) {
            this.errorText.set(S25Util.errorText(error, "Failed to save theme."));
        } finally {
            this.saving.set(false);
        }
    }

    async reset() {
        const persistedTheme = this.persistedTheme();
        this.hexError.set(false);
        this.errorText.set("");
        this.successText.set("");
        this.uploadedLogoData.set("");
        this.imageModel.resetImageState?.();
        this.logoImageUri.set(
            persistedTheme.logoBase64 ? S25Util.toBase64URI(persistedTheme.logoBase64, persistedTheme.logoType) : "",
        );
        this.logoWidthPx.set(persistedTheme.logoWidth);
        this.altText.set(persistedTheme.altText);
        this.backgroundColor.set(persistedTheme.backgroundColor);
        this.bottomBorderColor.set(persistedTheme.bottomBorderColor);
        this.textColor.set(persistedTheme.textColor);
        CacheRepository.invalidateByMethod("InstCssService.getInstCss");
        this.instCssResource.reload();
        await InstCssService.setInstCss();
    }

    private mapInstCssDataToThemeAttributes(cssData: InstCssData | undefined): ThemeAttributes {
        const normalizedCssData = cssData ?? {};
        return {
            logoBase64: normalizedCssData.logoBase64 ?? "",
            logoType: normalizedCssData.logoType ?? "png",
            logoWidth: this.normalizeLogoWidth(normalizedCssData.logoWidth),
            backgroundColor: this.normalizeHex(normalizedCssData.backgroundColor, "#46587c"),
            bottomBorderColor: this.normalizeHex(normalizedCssData.bottomBorderColor, "#04345d"),
            textColor: this.normalizeHex(normalizedCssData.textColor, "#ffffff"),
            altText: (normalizedCssData.altText ?? "").slice(0, ALT_TEXT_MAX_LENGTH),
        };
    }

    private async getThemeAttributes(): Promise<ThemeAttributes> {
        const imageData = (await this.imageModel.getImageData?.()) || this.logoImageUri();
        const logoBase64 = imageData ? S25Util.extractBase64FromURI(imageData) : "";
        const logoType = imageData ? S25Util.extractImageTypeFromURI(imageData) : "png";
        const logoWidth = this.imageModel.getWidth?.() || this.logoWidthPx() || 160;

        return {
            logoBase64,
            logoType,
            logoWidth,
            backgroundColor: this.backgroundColor(),
            bottomBorderColor: this.bottomBorderColor(),
            textColor: this.textColor(),
            altText: this.altText(),
        };
    }

    private normalizeLogoWidth(value: string | number | undefined): number {
        if (typeof value === "number") {
            return value;
        }
        if (typeof value === "string") {
            return S25Util.parseInt(value.replace("px", "")) || 160;
        }
        return 160;
    }

    private normalizeHex(value: string | undefined, fallback: string): string {
        const normalizedValue = this.ensureHash(value);
        return HEX_REGEX.test(normalizedValue) ? normalizedValue : fallback;
    }

    private ensureHash(value: string | undefined): string {
        const trimmedValue = (value ?? "").trim();
        if (!trimmedValue) {
            return "#";
        }
        return trimmedValue.startsWith("#") ? trimmedValue : `#${trimmedValue}`;
    }

    private normalizeLogoDataForComparison(imageData: string): string {
        const normalizedImageData = imageData ?? "";
        if (!normalizedImageData) {
            return "";
        }

        const logoType = S25Util.extractImageTypeFromURI(normalizedImageData) || "png";
        const logoBase64 = S25Util.extractBase64FromURI(normalizedImageData) || "";
        return `${logoType}|${logoBase64}`;
    }
}
